import _ from "lodash";
import { isOperational } from "./lifecycle";
import { DateTime } from "luxon";
import store from "@/store/store";
import { $time } from "./time";
import { extendContact, toApiContact } from "@/store/modules/contact";
import { fetchOnDemandOverlappingPredictions } from "@/js/api/predictions";

/*
   TODO:
   The logic in the plugin needs a big refactoring/rewiting.
   For instance there is some ambiguity about contact/allotment when finding conflicts in schedules.
   Allotments and contact should both be a 'scheduleEntity' (with padded start/end times etc. but its own properties).
   This work should be done when rewriting the 'contacts data workflow'.
*/

const generateScheduleForSystem = (contact, contacts, allotments = []) => {
	const schedule = contacts
		.concat(allotments)
		.filter(
			(scheduledEntity) => scheduledEntity.system.id === contact.system.id
		);
	const disableForMigration =
		!!(
			store.getters["environment/isProductionEnvironment"] ||
			store.getters["environment/isStagingEnvironment"]
		) && !store.getters["migratedAssets/systemIsMigrated"](contact.system);

	const { timeslots, conflicts, conflictLevel } = conflictAnalyze(
		contact,
		schedule
	);

	const internalTimeslots = conflictAnalyze(
		contact,
		contacts.filter(
			(scheduledEntity) => scheduledEntity.system.id === contact.system.id
		)
	);

	return {
		system: contact.system,
		schedule,
		contact,
		alternatives: timeslots,
		internalAlternatives: internalTimeslots,
		conflicts,
		conflictLevel,
		disableForMigration
	};
};

// Intersect utilities
const isOverlapping = (contactA, contactB) => {
	return (
		contactA.startTimePadded <= contactB.endTimePadded &&
		contactA.endTimePadded >= contactB.startTimePadded
	);
};

const adjustScheduleWithCartModifications = (schedule, contactCart) => {
	const movedContacts = contactCart.movedContacts.map((contact) => ({
		...contact,
		system: contact.selectedSystem
	}));
	const modifiedContacts = [
		...contactCart.createdContacts,
		...contactCart.cancelledContacts,
		...movedContacts
	];
	const scheduleWithoutModified = schedule.filter((contact) =>
		modifiedContacts.every((cont) => cont.id !== contact.id)
	);
	return [
		...scheduleWithoutModified,
		...contactCart.createdContacts,
		...movedContacts
	];
};

/*
 * The function conflictAnalyze finds all overlapping and non-overlapping (gap) time intervals between
 * the extendedContact and the conflicts in the schedule. The function returns an object with the following
 * properties:
 * - timeslots: A list of time intervals where the extendedContact does not overlap with any conflict in the schedule.
 * - conflicts: A list of time intervals where the extendedContact overlaps with a conflict in the schedule.
 * - conflictLevel: An integer value determining the conflict severity:
 *   - 0: No conflicts
 *   - 1: Conflicts with alternative timeslots
 *   - 2: Conflicts without alternative timeslots
 */
const conflictLevel = { low: 0, medium: 1, high: 2 };
const contactPadding = 1; // 1 second padding is needed to satisfy kogs restraints.

const conflictAnalyze = (extendedContact, schedule, minTimeslotLength = 1) => {
	const timeslots = [];
	const conflicts = [];
	const { startTimePadded, endTimePadded, setupDuration, teardownDuration } =
		extendedContact;
	const minTimeslotPadded =
		(setupDuration + teardownDuration + minTimeslotLength) * 1000;

	const conflictingContacts = _.chain(schedule)
		.filter(({ state }) => state !== "CANCELLED")
		.filter((contact) => isOverlapping(extendedContact, contact))
		.sortBy(["startTimePadded"])
		.value();

	if (!conflictingContacts.length) {
		return { timeslots, conflicts, conflictLevel: conflictLevel.low };
	}

	const addConflict = (startTime, endTime, contact) => {
		conflicts.push({
			contact,
			startTime,
			endTime,
			duration: endTime.toSeconds() - startTime.toSeconds()
		});
	};

	const addTimeslot = (startTimePadded, endTimePadded) => {
		const startTime = DateTime.fromSeconds(
			startTimePadded.toSeconds() + setupDuration + contactPadding
		).toUTC();
		const endTime = DateTime.fromSeconds(
			endTimePadded.toSeconds() - teardownDuration - contactPadding
		).toUTC();
		timeslots.push({
			...extendedContact,
			startTime,
			endTime,
			duration: endTime.toSeconds() - startTime.toSeconds(),
			startTimePadded,
			endTimePadded,
			durationPadded: endTimePadded.toSeconds() - startTimePadded.toSeconds()
		});
	};

	// Add algorithm stop element, no need to check for gaps/overlaps after this point.
	conflictingContacts.push({
		startTimePadded: endTimePadded,
		endTimePadded: endTimePadded
	});

	// Set start point to be the beginning of the extendedContact.
	let last_covered_point = startTimePadded;

	// Find all gaps longer than, or equal to, the minTimeslotLength.
	// Find all overlaps with the conflicting contacts.
	conflictingContacts.forEach((conflict) => {
		const gapSearchStart = conflict.startTimePadded;
		const gapSearchEnd = conflict.endTimePadded;

		if (
			gapSearchStart > last_covered_point &&
			gapSearchStart - last_covered_point >= minTimeslotPadded
		) {
			addTimeslot(last_covered_point, gapSearchStart);
		}
		last_covered_point = DateTime.max(last_covered_point, gapSearchEnd);

		const overlapSearchStart = DateTime.max(
			startTimePadded,
			conflict.startTimePadded
		);
		const overlapSearchEnd = DateTime.min(
			endTimePadded,
			conflict.endTimePadded
		);

		if (overlapSearchStart < overlapSearchEnd) {
			addConflict(overlapSearchStart, overlapSearchEnd, conflict);
		}
	});

	return {
		timeslots: timeslots.sort((a, b) => b.duration - a.duration),
		conflicts,
		conflictLevel: timeslots.length ? conflictLevel.medium : conflictLevel.high
	};
};

const isHistoric = (contact) => DateTime.utc() > contact.startTimePadded;

const isEditable = (contact) => {
	const timeThreshold = DateTime.min(
		contact.startTime.minus({ minutes: 1, seconds: 10 }),
		contact.startTimePadded.minus({ seconds: 10 })
	);
	return (
		!(
			contact.extendedState.isCancelled() || contact.extendedState.isCommitted()
		) && DateTime.utc() < timeThreshold
	);
};

const isSignable = (contact) => {
	return isHistoric(contact);
};

const formatScheduleReply = (contact) => {
	const start = contact.startTime;
	const end = contact.endTime;
	const system = contact.system.name;
	const spacecraft = contact.spacecraft.name;
	return `${start.toISODate()} ${system} ${spacecraft} ${$time.formatTime(start)}-${$time.formatTime(end)}`.trim();
};

const formatAsCSV = (contact) => {
	const start = contact.startTime;
	const end = contact.endTime;
	const system = contact.system.name;
	const spacecraft = contact.spacecraft.name;

	// get pass elevation
	let passPred = "Not found";
	if (contact?.ephemeris_id) {
		return fetchOnDemandOverlappingPredictions(
			[contact.ephemeris_id],
			contact.tenant.id,
			contact.spacecraft.id,
			[contact.system.station],
			contact.startTime,
			contact.endTime
		).then((predicted) => {
			if (predicted.predictions?.length == 1) {
				const contactPrediction = predicted.predictions[0] ?? null;
				try {
					passPred = `${Math.round(contactPrediction?.maximumElevation * 100) / 100}°`;
				} catch (error) {
					console.error(error);
				}
			}
			return [
				start.toISODate(),
				$time.formatTime(start),
				$time.formatTime(end),
				system,
				spacecraft,
				contact.missionProfile.name,
				passPred
			]
				.join(",")
				.trim();
		});
	}
	return Promise.resolve(
		[
			start.toISODate(),
			$time.formatTime(start),
			$time.formatTime(end),
			system,
			spacecraft,
			contact.missionProfile.name,
			passPred
		]
			.join(",")
			.trim()
	);
};

const getSharedTenant = (contacts) => {
	const tenantIds = [...new Set(contacts.map((contact) => contact.tenant.id))];
	return tenantIds.length === 1
		? store.getters["tenants/find"](tenantIds[0])
		: null;
};

const getSharedStation = (contacts) => {
	const stationIds = [
		...new Set(contacts.map((contact) => contact.system.station))
	];
	return stationIds.length === 1
		? store.getters["stations/find"](stationIds[0])
		: null;
};

const getSharedSystem = (contacts) => {
	const systemIds = [...new Set(contacts.map((contact) => contact.system.id))];
	return systemIds.length === 1
		? store.getters["systems/find"](systemIds[0])
		: null;
};

const getPossibleSystems = (contact) => {
	const onTenantSystems = (contact) => (systemID) => {
		const tenant = store.getters["tenants/find"](contact.missionProfile.tenant);
		return tenant.systems.includes(systemID);
	};

	return _.chain(contact.missionProfile.systems)
		.filter(onTenantSystems(contact))
		.mapStoreIds("systems")
		.filter((system) => contact.station.id === system.station)
		.filter((system) => isOperational(system))
		.value();
};

const getAllowedSystems = (contacts, schedule) => {
	const sharedStation = getSharedStation(contacts);

	const onTenantSystems = (contact) => (systemID) => {
		const tenant = store.getters["tenants/find"](contact.missionProfile.tenant);
		return tenant.systems.includes(systemID);
	};

	const onNoConflictWith = (contact) => (system) => {
		return !schedule
			.filter((contact) => !contact.extendedState?.isCancelled())
			.some(
				(scheduleEntity) =>
					contact.id !== scheduleEntity.id &&
					system.id === scheduleEntity.system.id &&
					isOverlapping(contact, scheduleEntity)
			);
	};

	return contacts.map((contact) =>
		_.chain(contact.missionProfile.systems)
			.filter(onTenantSystems(contact))
			.mapStoreIds("systems")
			.filter((system) => sharedStation?.id === system.station)
			.filter(onNoConflictWith(contact, schedule))
			.filter((system) => {
				return store.getters["environment/isProductionEnvironment"] ||
					store.getters["environment/isStagingEnvironment"]
					? !!store.getters["migratedAssets/systemIsMigrated"](system)
					: true;
			})
			.value()
	);
};

const getSelectableSystems = (contacts, schedule) => {
	const allowedSystems = getAllowedSystems(contacts, schedule);
	const sharedSystem = getSharedSystem(contacts);

	const onSharedSystems = (system) =>
		allowedSystems.every((systemList) =>
			systemList.some(({ id }) => id === system.id)
		);

	return allowedSystems[0]
		.filter((system) => onSharedSystems(system, allowedSystems))
		.filter((system) => !sharedSystem || sharedSystem.id !== system.id)
		.filter((system) => isOperational(system));
};

const handleOverlaps = (contact, schedule, minTrimRate) => {
	const contactSchedule = schedule.filter((cont) => cont.id !== contact.id);
	const minLength = Math.ceil((contact.duration * minTrimRate) / 100.0);
	const possibleSystems = getPossibleSystems(contact);

	const analyzedSystems = possibleSystems.map((system) => {
		const systemSchedule = contactSchedule.filter(
			(cont) => cont.system.id === system.id
		);
		return {
			system,
			...conflictAnalyze(contact, systemSchedule, minLength)
		};
	});

	const longestTimeslotsPerSystem = analyzedSystems.map((analysis) => {
		const longestTimeslotForSystem = _.maxBy(analysis.timeslots, "duration");
		return {
			...longestTimeslotForSystem,
			system: analysis.system
		};
	});

	const longestTimeslot = _.maxBy(longestTimeslotsPerSystem, "duration");

	if (longestTimeslot) {
		return {
			...contact,
			conflictType: "trimmable",
			trimmedTimeslot: longestTimeslot
		};
	}

	return {
		...contact,
		conflictType: "cancellable"
	};
};

const fixedSeed = 1000 + Math.round(1000 * Math.random());
let sequence = fixedSeed;

const seededRand = (min, max) => {
	let x = Math.sin(sequence++) * 10000;
	x = x - Math.floor(x);
	return Math.floor(x * (max - min)) + min;
};

const addReallocateOptionsToContacts = (contacts, schedule, minTrimRate) => {
	sequence = fixedSeed; // Reseting random sequence to get the same systems every time and prevent jumping behaivour on update
	const occupied = [];

	return contacts.map((contact) => {
		if (contact?.isAllotment) {
			return { ...contact, conflictType: "allotment" };
		}
		if (isHistoric(contact)) {
			return { ...contact, conflictType: "ongoing" };
		}
		if (!isEditable(contact)) {
			return { ...contact, conflictType: "tooclose" };
		}

		let selectableSystems = getSelectableSystems([contact], schedule);

		if (!selectableSystems.length) {
			return handleOverlaps(contact, schedule, minTrimRate);
		}

		if (
			selectableSystems.some(
				(system) => system.dedicatedTo === contact.tenant.id
			)
		) {
			// If found, only use dedicated systems as selectable.
			selectableSystems = selectableSystems.filter(
				(system) => system.dedicatedTo === contact.tenant.id
			);
		} else if (selectableSystems.some((system) => system.dedicatedTo)) {
			// If the system is dedicated to another tenant and other options are available, remove it from selectable systems
			const systemsDedicatedToOther = selectableSystems.filter(
				(system) => system.dedicatedTo
			);
			if (selectableSystems.length > systemsDedicatedToOther.length) {
				selectableSystems = selectableSystems.filter(
					(system) => !system.dedicatedTo
				);
			}
		}

		const selectableSystemsSorted = _.chain(selectableSystems)
			.sortByAlphaNum()
			.value();

		if (
			selectableSystemsSorted.every((system) => occupied.includes(system.id))
		) {
			occupied.length = 0;
		}

		let selectedSystem =
			selectableSystemsSorted[seededRand(0, selectableSystemsSorted.length)];

		// Keeping track of which systems have been used for a more even distribution
		while (occupied.includes(selectedSystem.id)) {
			selectedSystem =
				selectableSystemsSorted[seededRand(0, selectableSystemsSorted.length)];
		}

		occupied.push(selectedSystem.id);

		return {
			...contact,
			conflictType: "movable",
			selectableSystems,
			selectedSystem
		};
	});
};

const updateContactTimes = (contact, newStartTime, newEndTime) => {
	contact.startTime = newStartTime;
	contact.endTime = newEndTime;
	contact.duration = newEndTime.toSeconds() - newStartTime.toSeconds();
	contact.durationPadded =
		contact.duration + contact.teardownDuration + contact.setupDuration;
	contact.startTimePadded = contact.startTime.minus({
		seconds: contact.setupDuration
	});
	contact.endTimePadded = contact.endTime.plus({
		seconds: contact.teardownDuration
	});
	return contact;
};

export default {
	install: (app) => {
		app.config.globalProperties.$contact = {
			extendContact,
			toApiContact,
			isHistoric,
			isEditable,
			isSignable,
			isOverlapping,
			formatScheduleReply,
			formatAsCSV,
			generateScheduleForSystem,
			adjustScheduleWithCartModifications,
			conflictAnalyze,
			getSharedTenant,
			getSharedStation,
			getSharedSystem,
			getAllowedSystems,
			getSelectableSystems,
			addReallocateOptionsToContacts,
			updateContactTimes
		};
	}
};
